// src/pages/ChatPage.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { apiRequest } from "../services/api";

function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(LanguageContext);

  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [text, setText] = useState("");

  const bottomRef = useRef(null);
  const [showWhatsappCTA, setShowWhatsappCTA] = useState(true);

  const whatsappMessage =
    lang === "EN" ? "I need help" : "أحتاج مساعدة";

  const whatsappLink = `https://wa.me/96170203205?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const TEXT = {
    EN: {
      title: "Support Chat",
      placeholder: "Type your message…",
      send: "Send",
    },
    AR: {
      title: "محادثة الدعم",
      placeholder: "اكتبي رسالتك…",
      send: "إرسال",
    },
  }[lang];

  /* Load thread */
  useEffect(() => {
    let interval;

    async function load() {
      let list;
      try {
        list = await apiRequest("/contact-messages/mine");
      } catch {
        navigate(-1);
        return;
      }

      const targetId =
        location.state?.messageId || list?.[0]?.id;

      if (!targetId) return;

      setThreadId(targetId);

      const thread = list.find((m) => m.id === targetId);
      if (thread) {
        setMessages(thread.replies || []);
      }

      await apiRequest(`/contact-messages/${targetId}/read`, {
        method: "POST",
      });

      interval = setInterval(async () => {
        const fresh = await apiRequest("/contact-messages/mine");
        const updated = fresh.find((m) => m.id === targetId);
        if (updated) {
          setMessages(updated.replies || []);
        }
      }, 4000);
    }

    load();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("esill-token")) {
      navigate(-1);
    }
  }, []);

  /* Auto scroll */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* Send */
  const send = async () => {
    if (!text.trim() || !threadId) return;

    await apiRequest(`/contact-messages/${threadId}/user-reply`, {
      method: "POST",
      body: JSON.stringify({ body: text }),
    });

    setMessages((m) => [
      ...m,
      { sender_type: "user", body: text },
    ]);

    setText("");
  };

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] flex flex-col text-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* HEADER */}
      <div className="fixed top-0 left-0 w-full bg-[#121F17] border-b border-[#2E5A2A] px-4 py-4 flex items-center gap-3 z-50">
        <button
          onClick={() => navigate(-1)}
          className="text-2xl opacity-70 hover:opacity-100"
        >
          ×
        </button>
        <h1 className="text-sm font-semibold tracking-wide">
          {TEXT.title}
        </h1>
      </div>

      {/* CHAT */}
      <div className="flex-1 pt-20 px-4 pb-28 space-y-3 overflow-y-auto">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${
              m.sender_type === "user"
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-[75%] text-sm px-4 py-2 rounded-2xl ${
                m.sender_type === "user"
                  ? "bg-[#2E5A2A] text-white rounded-br-sm"
                  : "bg-[#1A2A1E] border border-[#2E5A2A] rounded-bl-sm"
              }`}
            >
              {m.body}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="fixed bottom-0 left-0 w-full bg-[#121F17] border-t border-[#2E5A2A] px-3 py-3 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={TEXT.placeholder}
          disabled={!threadId}
          className="
            flex-1 bg-[#0F1A12] border border-[#2E5A2A]
            rounded-full px-4 py-2 text-sm
            text-white placeholder-[#9FB39C]
            focus:outline-none
            disabled:opacity-50
          "
        />
        <button
          onClick={send}
          disabled={!threadId}
          className="
            px-5 py-2 bg-[#2E5A2A] text-white
            rounded-full text-sm font-semibold
            hover:brightness-110 active:scale-95
            disabled:opacity-50
          "
        >
          {TEXT.send}
        </button>
      </div>

      {/* WHATSAPP CTA */}
      {showWhatsappCTA && (
        <div
          className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2"
          dir="ltr"
        >
          <button
            onClick={() => setShowWhatsappCTA(false)}
            className="
              w-7 h-7 rounded-full
              bg-[#121F17] border border-[#2E5A2A]
              flex items-center justify-center
              text-white text-sm shadow-md
            "
          >
            ×
          </button>

          <a
            href={whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="
              w-14 h-14 rounded-full
              flex items-center justify-center
              shadow-lg active:scale-95 transition
            "
            style={{ backgroundColor: "#25D366" }}
          >
            <img
              src="/icons/whatsapp.png"
              alt="WhatsApp"
              className="w-7 h-7"
            />
          </a>
        </div>
      )}
    </div>
  );
}

export default ChatPage;
